/**
 * React hook to Determine how a layerhost should invoke a dxl Query
 *
 * @module utils/useLayerHostMeta
 */

import { useEffect, useRef } from 'react';

import Cookies from 'js-cookie';

import { useLayerHostContext } from '@ulta/core/providers/LayerHostProvider/LayerHostProvider';
import constants from '@ulta/core/utils/constants/constants';
import { handleReplace } from '@ulta/core/utils/handleLocation/handleLocation';
import { getStorage, setStorage } from '@ulta/core/utils/storage/storage';
import { STORAGE_KEY } from '@ulta/core/utils/storageKeys/storageKeys';

import { PAGE_MODULE_NAME } from '../../components/Page/Page';
import { useAppConfigContext } from '../../providers/AppConfigProvider/AppConfigProvider';
import { usePageDataContext } from '../../providers/PageDataProvider/PageDataProvider';
import { useSnackBar } from '../../providers/SnackBarProvider/SnackBarProvider';
import { useUserContext } from '../../providers/UserContextProvider/UserContextProvider';
import { DATACAPTURE_UNRESOLVED_KEY, emitTypeAheadData } from '../../utils/datacapture/datacapture';
import { debugModuleName, devLogger, LOG_TOPIC } from '../../utils/devMode/devMode';
import { handleEmptyObjects } from '../../utils/handleEmptyObjects/handleEmptyObjects';
import * as utils from './useLayerHostMeta';

/**
 * Handles the meta object from the response
 * @param {object} data arguments
 * @param {object} data.meta meta object from the response, we look for initAction or snackBar
 */
export const useLayerHostMeta = ( data ) => {
  const { meta = {}, props = {}, loading } = handleEmptyObjects( data );
  const { initAction, snackBar, searchData, sessionData, sessionAction, disableInitAction, postAuth0Action } = meta;
  const { showSnackBar } = useSnackBar();
  const { updateSession, processSessionAction, isInitialSessionPing, isProcessingAuthAction } = useUserContext();
  const { isLocal, websiteDomain } = useAppConfigContext();
  const { pageLastUpdated } = usePageDataContext();
  const { killSwitchActivated } = useLayerHostContext();

  const hasProcessedSearchDataCapture = useRef( false );
  const hasProcessedSessionAction = useRef( false );
  const sessionActionLastProcessed = useRef( 0 );

  useEffect( () => {
    if( disableInitAction ){
      devLogger( `killSwitchActivated | ${debugModuleName( props )}`, 0, LOG_TOPIC.Session );
      killSwitchActivated.current = true;
    }
  }, [disableInitAction] );


  /*
   * Handle resetting hasProcessedSessionAction
   * -> if this is the "initial session ping" to DXL from the root page's sessionAction,
   *    we never want to re-process it
   * -> If the layer's componentLastUpdated has changed and there is a new sessionAction,
   *    we reset the flag so that we can process it again
   */
  useEffect( () => {
    // Only process the Page sessionAction one time, otherwise compare to the layer's componentLastUpdated
    const hasProcessed =
      ( !isInitialSessionPing?.current && props.moduleName === PAGE_MODULE_NAME ) ||
      sessionActionLastProcessed.current === props.componentLastUpdated;

    if( hasProcessed || !sessionAction?.graphql ){
      return;
    }

    // first time session current will be pageLastUpdated when there is a pageModuleName otherwise componentLastUpdated
    const lastProcessed = props.moduleName === PAGE_MODULE_NAME ? pageLastUpdated : props.componentLastUpdated;
    sessionActionLastProcessed.current = lastProcessed;

    hasProcessedSessionAction.current = false;

    if( isInitialSessionPing && isInitialSessionPing?.current ){
      isInitialSessionPing.current = false;
    }

    devLogger( `reset processSessionAction | ${debugModuleName( props )}`, 0, LOG_TOPIC.Session );
  }, [props.componentLastUpdated, sessionAction] );

  /*
   * Processes incoming session action from DXL, sends it up to UserContextProvider
   */
  useEffect( () => {
    if( hasProcessedSessionAction.current || !sessionAction?.graphql || !processSessionAction ){
      return;
    }

    // Sometimes we want to execute session flow w/ postAuth0Action when the user is post login
    // We do that in the utility below, attaching code/nonce to the sessionAction
    const derivedSessionAction = utils.getDerivedSessionAction( {
      sessionAction,
      postAuth0Action,
      isProcessingAuthAction,
      isLocal,
      websiteDomain
    } );

    devLogger( `processSessionAction | ${debugModuleName( props )}`, 0, LOG_TOPIC.Session );
    hasProcessedSessionAction.current = true;

    processSessionAction( { sessionAction: derivedSessionAction, props } );
  }, [processSessionAction, sessionAction, postAuth0Action, isLocal, websiteDomain] );


  /*
   * Listens for and merges incoming session data from DXL
   */
  useEffect( () => {
    if( !sessionData || loading || !updateSession ){
      return;
    }

    devLogger( {
      topic: LOG_TOPIC.Session,
      title: `session update | ${debugModuleName( props )}`,
      value: sessionData
    } );

    updateSession( { sessionData } );
  }, [updateSession, loading, sessionData] );




  /*
   * Listens for snackbars
   */
  useEffect( () => {
    if( !snackBar ){
      return;
    }

    // Delay showing snackbars for a bit until the UI re-renders. When we update the user session
    // while trying to animate a snackbar, there's too much lag and the Snackbar will animate before the UI update
    setTimeout( () => {
      showSnackBar( snackBar );
    }, 300 );
  }, [snackBar] );


  /*
   * Listens for search redirect
   */
  useEffect( () => {
    const isSearchRedirect =
      !!initAction?.url && initAction.clientActionParams?.eventNames?.includes( SEARCH_REDIRECT_EVENT );


    if( !isSearchRedirect || hasProcessedSearchDataCapture.current ){
      return;
    }

    devLogger( { title: 'Typeahead DC > Saving Redirect', value: initAction.url, collapsed: true } );

    hasProcessedSearchDataCapture.current = true;

    emitTypeAheadData( { targetUrl: initAction.url } );
  }, [initAction] );


  /*
   * Listens for searchCount
   */
  useEffect( () => {
    // We need to check for null because the resultCount can be 0
    const hasResultCount = typeof searchData?.resultCount !== 'undefined' && searchData?.resultCount !== null;

    if( !hasResultCount || hasProcessedSearchDataCapture.current ){
      return;
    }

    devLogger( { title: 'Typeahead DC > Dispatching Result Count', value: searchData, collapsed: true } );

    hasProcessedSearchDataCapture.current = true;

    const typeAheadData = {
      [DATACAPTURE_UNRESOLVED_KEY.SearchResult]: searchData.resultCount.toString()
    };

    emitTypeAheadData( { fireEvent: true, typeAheadData } );
  }, [searchData] );
};

/**
 * @method getDerivedSessionAction
 * @summary this method is responsible for deriving the session action
 * @param  {object} data - arguments
 * @param  {object} data.sessionAction - DXL session action
 * @param  {object} data.postAuth0Action - DXL post auth0 action
 * @param  {object} data.isProcessingAuthAction - flag to check if we are processing an auth0 action
 * @param  {boolean} data.isLocal - flag to check if we are in a local environment
 * @param  {string} data.websiteDomain - website domain
 * @returns {object} derivedAction
 */
export const getDerivedSessionAction = ( data ) => {
  const { sessionAction, postAuth0Action, isProcessingAuthAction = {}, isLocal, websiteDomain } = data || {};

  const { code, nonce } = utils.getAuthParams();

  let derivedAction = sessionAction;
  let isPostAuth0Action = false;
  const moduleParams = {};

  if( !!code && !!nonce && !!postAuth0Action ){
    isPostAuth0Action = true;

    utils.removeAuthParams();
    moduleParams.authCode = code;
    moduleParams.authNonce = nonce;
  }

  // If we have an email validation cookie, we need to pass it and use postAuth0Action
  const emailStatus = Cookies.get( constants.AUTH_EMAIL_TRANSPORT );
  if( emailStatus ){
    isPostAuth0Action = true;
    moduleParams.updateEmailStatus = emailStatus === 'true';
  }

  // If we've queued moduleParams, derive the action
  if( isPostAuth0Action ){
    isProcessingAuthAction.current = true;

    derivedAction = {
      ...postAuth0Action,
      variables: {
        moduleParams
      }
    };
  }

  // In local development envs we need to tell BE the redirectUri so they can redirect back to the correct domain
  // in lower environments. Typically this is stored in a configmap in k8s - but we don't have that in local
  if( isPostAuth0Action && isLocal ){
    derivedAction.variables.moduleParams.redirectUri = websiteDomain;
  }

  return derivedAction;
};

/**
 * @method getAuthParams
 * @summary this method is responsible for getting the auth params from the url
 * @returns {object} { code, nonce }
 */
export const getAuthParams = ( ) => {
  const [code, nonce] = Cookies.get( constants.AUTH_COOKIE_TRANSPORT )?.split( '|' ) || [];

  // check in local storage if the nonce has been processed already and early return if so
  const prev = getStorage( { secure: true, key: STORAGE_KEY.auth0NonceProcessed } ) || {};
  const hasProcessedCode = nonce === prev.previousNonce;

  if( !code || !nonce || hasProcessedCode ){
    return {};
  }

  // store the nonce in local storage so we can check if it has been processed already
  setStorage( { secure: true, key: STORAGE_KEY.auth0NonceProcessed, value: { previousNonce: nonce } } );

  return { code, nonce };
};

/**
 * @method removeAuthParams
 * @summary this method is responsible for removing the auth params from the url
 * @returns {void}
 */
export const removeAuthParams = () => {
  const url = new URL( global.location.href );
  url.searchParams.delete( 'code' );
  url.searchParams.delete( 'nonce' );

  handleReplace( { url: url.toString() } );
};



/**
 * @const {string} SEARCH_REDIRECT_EVENT - Search redirect event name
 */
export const SEARCH_REDIRECT_EVENT = 'searchRedirect';
